// SignupPage — 2-step wizard com progress (Stepper). Submit final POST /auth/signup
// → conta criada PENDENTE (sem sessão) → tela "aguardando autorização".
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { Stepper } from '@/components/ui/Stepper';
import { ArrowLeftIcon, ArrowRightIcon, ClockIcon } from '@/components/icons';
import { ROUTES } from '@/app/router/paths';
import { useGoogleSignupMutation, useSignupMutation } from '../api';
import type { Segment, TeamSize } from '../api';
import { GoogleButton } from '../components/GoogleButton';
import { useGoogleAuth, type GoogleRouteState } from '../useGoogleAuth';
import { parseAuthError } from '../errors';
import { LogoMark } from './shared/LogoMark';
import styles from './SignupPage.module.css';

const step1Schema = z
  .object({
    name: z.string().min(2, 'Diga seu nome completo.'),
    email: z.string().email('Informe um e-mail válido.'),
    password: z.string().min(8, 'A senha precisa ter no mínimo 8 caracteres.'),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, {
    path: ['confirm'],
    message: 'As senhas não coincidem.',
  });

const step2Schema = z.object({
  companyName: z.string().min(2, 'Informe o nome da empresa.'),
  segment: z.enum(['GRAFICA', 'ECOMMERCE', 'CLINICA', 'SERVICOS', 'RESTAURANTE']),
  teamSize: z.enum(['S', 'M', 'L', 'XL']),
});

type Step1 = z.infer<typeof step1Schema>;
type Step2 = z.infer<typeof step2Schema>;

const SEGMENT_LABEL: Record<Segment, string> = {
  GRAFICA: 'Gráfica / Impressão',
  ECOMMERCE: 'E-commerce',
  CLINICA: 'Clínica / Saúde',
  SERVICOS: 'Serviços',
  RESTAURANTE: 'Restaurante / Delivery',
};

const TEAM_SIZES: { value: TeamSize; label: string }[] = [
  { value: 'S', label: '1–3' },
  { value: 'M', label: '4–10' },
  { value: 'L', label: '11–25' },
  { value: 'XL', label: '25+' },
];

export default function SignupPage() {
  const location = useLocation();
  const navigate = useNavigate();
  // Quem chegou pelo botão do Google e ainda não tem conta: o /auth/google devolveu
  // GOOGLE_SIGNUP_REQUIRED e o useGoogleAuth mandou para cá com o token e o que o
  // Google já sabe. Falta só a empresa — o passo 1 (nome/senha) não se aplica.
  const google = (location.state as GoogleRouteState | null)?.google ?? null;

  const [step, setStep] = useState<0 | 1>(google ? 1 : 0);
  const [step1Data, setStep1Data] = useState<Step1 | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [signup, { isLoading, error }] = useSignupMutation();
  const [googleSignup, { isLoading: googleLoading, error: googleError }] =
    useGoogleSignupMutation();
  const googleAuth = useGoogleAuth();
  const toast = useToast();

  // Voltar da tela de login com o consentimento já feito pula o passo de senha.
  useEffect(() => {
    if (google) setStep(1);
  }, [google]);

  useEffect(() => {
    const falha = error ?? googleError;
    if (falha) toast.danger({ title: 'Falha ao criar conta', description: parseAuthError(falha) });
  }, [error, googleError, toast]);

  const step1Form = useForm<Step1>({ resolver: zodResolver(step1Schema), mode: 'onBlur' });
  const step2Form = useForm<Step2>({
    resolver: zodResolver(step2Schema),
    defaultValues: { segment: 'GRAFICA', teamSize: 'M' },
  });

  const onSubmitStep1 = step1Form.handleSubmit((v) => {
    setStep1Data(v);
    setStep(1);
  });

  const onSubmitStep2 = step2Form.handleSubmit(async (v) => {
    // Cadastro por Google: o MESMO token volta ao back, que o verifica de novo. Nome e
    // e-mail não são enviados — quem os afirma é o token, não este formulário.
    if (google) {
      try {
        await googleSignup({
          idToken: google.idToken,
          company: { name: v.companyName, segment: v.segment, teamSize: v.teamSize },
        }).unwrap();
        setSubmitted(true);
      } catch {
        /* mostra erro via banner */
      }
      return;
    }
    if (!step1Data) {
      setStep(0);
      return;
    }
    try {
      await signup({
        name: step1Data.name,
        email: step1Data.email,
        password: step1Data.password,
        company: {
          name: v.companyName,
          segment: v.segment,
          teamSize: v.teamSize,
        },
      }).unwrap();
      // Conta criada PENDENTE — não há sessão. Mostra confirmação de espera.
      setSubmitted(true);
    } catch {
      /* mostra erro via banner */
    }
  });

  if (submitted) {
    return (
      <main aria-labelledby="signup-pending-title">
        <LogoMark />
        <div className={styles.pendingIcon} aria-hidden="true">
          <ClockIcon size={28} />
        </div>
        <h1 id="signup-pending-title" className={styles.title}>
          Conta criada!
        </h1>
        <p className={styles.subtitle}>
          {google
            ? 'Sua conta está aguardando autorização. Assim que for liberada, é só entrar com o Google.'
            : 'Sua conta está aguardando autorização. Assim que for liberada, você poderá entrar normalmente com seu e-mail e senha.'}
        </p>
        <p className={styles.footer}>
          <Link to={ROUTES.login} className={styles.link}>
            Voltar para o login
          </Link>
        </p>
      </main>
    );
  }

  return (
    <main aria-labelledby="signup-title">
      <LogoMark />
      <div className={styles.stepperWrap}>
        <Stepper current={step} total={2} label={`Passo ${step + 1} de 2`} />
      </div>
      <h1 id="signup-title" className={styles.title}>
        {step === 0 ? 'Crie sua conta' : 'Conte sobre seu negócio'}
      </h1>
      <p className={styles.subtitle}>
        {step === 0
          ? 'Comece grátis. Sem cartão de crédito.'
          : 'Vamos personalizar a IA para o seu segmento.'}
      </p>

      {google && step === 1 && (
        <p className={styles.subtitle}>
          Entrando como <strong>{google.email}</strong>.
        </p>
      )}

      {step === 0 && (
        <form className={styles.form} onSubmit={onSubmitStep1} noValidate>
          <Input
            label="Seu nome"
            autoComplete="name"
            error={step1Form.formState.errors.name?.message}
            {...step1Form.register('name')}
          />
          <Input
            label="E-mail de trabalho"
            type="email"
            autoComplete="email"
            error={step1Form.formState.errors.email?.message}
            {...step1Form.register('email')}
          />
          <Input
            label="Senha"
            type="password"
            autoComplete="new-password"
            error={step1Form.formState.errors.password?.message}
            {...step1Form.register('password')}
          />
          <Input
            label="Confirmar senha"
            type="password"
            autoComplete="new-password"
            error={step1Form.formState.errors.confirm?.message}
            {...step1Form.register('confirm')}
          />
          <div className={styles.actionRow}>
            <Button
              type="button"
              variant="secondary"
              size="lg"
              disabled
              leadingIcon={<ArrowLeftIcon size={18} />}
            >
              Voltar
            </Button>
            <Button
              type="submit"
              size="lg"
              trailingIcon={<ArrowRightIcon size={18} />}
              className={styles.grow}
            >
              Continuar
            </Button>
          </div>
        </form>
      )}

      {step === 0 && (
        <>
          <div className={styles.divider}>
            <span>ou</span>
          </div>
          <GoogleButton
            onCredential={googleAuth.onCredential}
            text="signup_with"
            className={styles.fullWidth}
          />
        </>
      )}

      {step === 1 && (
        <form className={styles.form} onSubmit={onSubmitStep2} noValidate>
          <Input
            label="Nome da empresa"
            error={step2Form.formState.errors.companyName?.message}
            {...step2Form.register('companyName')}
          />
          <label className={styles.selectField}>
            <span className={styles.selectLabel}>Segmento</span>
            <select className={styles.select} {...step2Form.register('segment')}>
              {(Object.keys(SEGMENT_LABEL) as Segment[]).map((seg) => (
                <option key={seg} value={seg}>
                  {SEGMENT_LABEL[seg]}
                </option>
              ))}
            </select>
          </label>

          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>Tamanho da equipe</legend>
            <div className={styles.teamRow}>
              {TEAM_SIZES.map((opt) => (
                <label key={opt.value} className={styles.teamOption}>
                  <input type="radio" value={opt.value} {...step2Form.register('teamSize')} />
                  {opt.label}
                </label>
              ))}
            </div>
          </fieldset>

          <div className={styles.actionRow}>
            <Button
              type="button"
              variant="secondary"
              size="lg"
              // Quem veio do Google não tem passo 1: não há nome nem senha para revisar.
              // O caminho de volta dele é a tela de login, não um formulário vazio.
              onClick={() => (google ? navigate(ROUTES.login) : setStep(0))}
              leadingIcon={<ArrowLeftIcon size={18} />}
            >
              Voltar
            </Button>
            <Button
              type="submit"
              size="lg"
              loading={isLoading || googleLoading}
              trailingIcon={<ArrowRightIcon size={18} />}
              className={styles.grow}
            >
              Criar conta
            </Button>
          </div>
        </form>
      )}

      <p className={styles.footer}>
        Já tem conta?{' '}
        <Link to={ROUTES.login} className={styles.link}>
          Entrar
        </Link>
      </p>
    </main>
  );
}
